import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { TIME_BLOCKS, MAX_ITEMS_PER_BLOCK, JOURNAL_QUESTIONS } from '../../utils/todoConstants';
import './TodoTab.css';

export default function TodoTab({ clientId, client }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newItemText, setNewItemText] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    loadItems();
  }, [clientId]);

  async function loadItems() {
    setLoading(true);
    const { data } = await supabase
      .from('todo_items')
      .select('*')
      .eq('client_id', clientId)
      .eq('is_active', true)
      .order('sort_order')
      .order('created_at');

    setItems(data || []);
    setLoading(false);
    setDirty(false);
  }

  function getBlockItems(blockKey) {
    return items.filter((i) => i.time_block === blockKey);
  }

  async function addItem(blockKey) {
    const text = (newItemText[blockKey] || '').trim();
    if (!text) return;

    const blockItems = getBlockItems(blockKey);
    if (blockItems.length >= MAX_ITEMS_PER_BLOCK) {
      alert(`Maximum ${MAX_ITEMS_PER_BLOCK} items per section.`);
      return;
    }

    const { data, error } = await supabase
      .from('todo_items')
      .insert({
        client_id: clientId,
        name: text,
        time_block: blockKey,
        sort_order: blockItems.length,
      })
      .select()
      .single();

    if (data) {
      setItems((prev) => [...prev, data]);
      setNewItemText((prev) => ({ ...prev, [blockKey]: '' }));
    }
  }

  async function removeItem(itemId) {
    await supabase
      .from('todo_items')
      .update({ is_active: false })
      .eq('id', itemId);

    setItems((prev) => prev.filter((i) => i.id !== itemId));
  }

  function startEdit(item) {
    setEditingId(item.id);
    setEditText(item.name);
  }

  async function saveEdit(itemId) {
    const text = editText.trim();
    if (!text) return;

    await supabase
      .from('todo_items')
      .update({ name: text, updated_at: new Date().toISOString() })
      .eq('id', itemId);

    setItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, name: text } : i))
    );
    setEditingId(null);
    setEditText('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditText('');
  }

  function handleKeyDown(e, blockKey) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem(blockKey);
    }
  }

  function handleEditKeyDown(e, itemId) {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit(itemId);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  }

  if (loading) {
    return <div className="tab-loading">Loading to-do list...</div>;
  }

  return (
    <div className="todo-tab">
      <div className="todo-tab-header">
        <h3 className="todo-tab-title">To-Do List</h3>
        <p className="todo-tab-subtitle">
          Items appear on {client.name.split(' ')[0]}'s daily view. They check them off each day.
        </p>
      </div>

      {TIME_BLOCKS.map((block) => {
        const blockItems = getBlockItems(block.key);
        const atMax = blockItems.length >= MAX_ITEMS_PER_BLOCK;

        return (
          <div key={block.key} className="todo-block">
            <div className="todo-block-header">
              <span className="todo-block-icon">{block.icon}</span>
              <span className="todo-block-label">{block.label}</span>
              <span className="todo-block-count">
                {blockItems.length}/{MAX_ITEMS_PER_BLOCK}
              </span>
            </div>

            <div className="todo-block-items">
              {blockItems.map((item, idx) => (
                <div key={item.id} className="todo-item-row">
                  <span className="todo-item-num">{idx + 1}.</span>
                  {editingId === item.id ? (
                    <div className="todo-item-edit-row">
                      <input
                        type="text"
                        className="todo-item-edit-input"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, item.id)}
                        autoFocus
                      />
                      <button
                        className="todo-item-save-btn"
                        onClick={() => saveEdit(item.id)}
                      >
                        ✓
                      </button>
                      <button
                        className="todo-item-cancel-btn"
                        onClick={cancelEdit}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <span
                        className="todo-item-name"
                        onClick={() => startEdit(item)}
                        title="Click to edit"
                      >
                        {item.name}
                      </span>
                      <button
                        className="todo-item-remove"
                        onClick={() => removeItem(item.id)}
                        title="Remove item"
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
              ))}

              {blockItems.length === 0 && (
                <p className="todo-block-empty">No items yet</p>
              )}
            </div>

            {!atMax && (
              <div className="todo-add-row">
                <input
                  type="text"
                  className="todo-add-input"
                  placeholder={`Add ${block.label.toLowerCase()} item...`}
                  value={newItemText[block.key] || ''}
                  onChange={(e) =>
                    setNewItemText((prev) => ({
                      ...prev,
                      [block.key]: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => handleKeyDown(e, block.key)}
                />
                <button
                  className="todo-add-btn"
                  onClick={() => addItem(block.key)}
                  disabled={!(newItemText[block.key] || '').trim()}
                >
                  + Add
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Journal questions preview */}
      <div className="todo-journal-preview">
        <h4 className="todo-journal-title">Daily Journal Prompts</h4>
        <p className="todo-journal-subtitle">
          These appear at the bottom of the client's daily view.
        </p>
        {JOURNAL_QUESTIONS.map((jq) => (
          <div key={jq.key} className="todo-journal-item">
            <span className="todo-journal-q">{jq.question}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
